import type {
    DeserializedGridCellParams,
    DeserializedGridColDef,
    JSONSerializableGridColDef
} from "./MuiXColumnDefinitions.ts";
import moment from "moment";
import {CurrencyCell} from "./cells/CurrencyCell.tsx";
import React from "react";
import type {
    PartGridRowModel
} from "../../pages/categorization/components/review-table/review-table-muix/CategorizationTableDataModel.ts";
import {GoogleLinkWrapper} from "./cells/wrapper/GoogleLinkWrapper.tsx";
import type {CustomTypes, JSONSerializableColumnType} from "./cells/MuiXBasicColumnTypes.ts";
import type {GridColType} from "@mui/x-data-grid-premium";
import {AdminPanelSettings} from "@mui/icons-material";
import {getSupplierIdHack} from "../../stores/hacks/display.ts";

/**
 * Deserialize a column specification
 * @param serializedSpec
 * @param isMithraStaffGroup
 */
export function toMuiXBasicColumn<T extends CustomTypes>(serializedSpec: JSONSerializableGridColDef<T>, isMithraStaffGroup: boolean): DeserializedGridColDef<T> {
    const serializableType = serializedSpec.serializableType;
    // console.log('toMuiXBasicColumn', serializableType)
    if (!serializableType) {
        throw new Error(`Unsupported type: ${serializableType}`);
    }

    const spec: DeserializedGridColDef<T> = {...serializedSpec}
    if (!spec.type) {
        spec.type = getMuiXDefaultType(serializableType);
    }
    if (!spec.headerAlign) {
        // Keep headers left even when column type defaults to right (e.g. number)
        spec.headerAlign = 'left';
    }

    if (spec.groupable === undefined) {
        // TODO: Not sure about this
        spec.groupable = false;
    }
    if (spec.aggregable === undefined) {
        // TODO: Not sure about this
        spec.aggregable = false;
    }

    if (isMithraStaffGroup) {
        const renderHeader = spec.renderHeader ? spec.renderHeader : () => spec.headerName;
        spec.renderHeader = (params) => <>
            {renderHeader(params)}
            <div className="mithra-table-cell-dev">
                <span>{spec.isMithraStaffOnly && <AdminPanelSettings/>} {spec.field}</span>
            </div>
        </>;
    }
    if (spec.googleLink) {
        wrap(spec, GoogleLinkWrapper);
    }
    switch (serializableType) {
        case "date":
        case "dateTime":
            return {
                valueFormatter: value => value ? moment(value).format('L') : '-',
                ...spec,
            };
        case "string-supplier-id":
            return {
                valueGetter: (value, row) => {
                    if (!row) return undefined;
                    const r = row as PartGridRowModel;
                    return getSupplierIdHack(r);
                },
                ...spec,
            }
        case "number-currency":
            return {
                renderCell: CurrencyCell,
                // valueFormatter: (value) => {
                //     if (!value) {
                //         return value;
                //     }
                //     return currencyFormatter.format(value);
                // },
                ...spec,
            };
        default:
        case "actions":
        case "boolean":
        case "custom":
        case "number":
        case "singleSelect":
        case "string":
            return spec
    }
}

/**
 * @param spec
 * @param Wrapper React component that will wrap the cell content. (no type checking for simplicity)
 */
export function wrap<T extends CustomTypes>(spec: DeserializedGridColDef<T>, Wrapper: React.FC<DeserializedGridCellParams>): void {
    const renderCell = spec.renderCell;
    if (renderCell) {
        // Chain it
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        spec.renderCell = (params) => <Wrapper {...(params as any)}>{renderCell(params)}</Wrapper>
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        spec.renderCell = (params) => <Wrapper {...(params as any)}>{params.value}</Wrapper>
    }
}

function getMuiXDefaultType<T extends CustomTypes>(serializedType: JSONSerializableColumnType<T>): GridColType {
    switch (serializedType) {
        case 'string':
        case 'number':
        case 'date':
        case 'dateTime':
        case 'boolean':
        case 'singleSelect':
        case 'actions':
        case 'custom':
            return serializedType;
    }
    if (serializedType.startsWith('number')) {
        return 'number';
    }
    if (serializedType.startsWith('string')) {
        return 'string';
    }
    return 'custom';
}
